import React, { useState, useEffect } from 'react';
import { ExternalLink, Calendar, DollarSign, Tag } from 'lucide-react';
import { projectsAPI } from '../services/api';

function Projects() {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll(200);
      setProjects(response.data.projects || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch projects');
      console.error('Error fetching projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.project_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.project_description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || project.project_type === filterType;
    return matchesSearch && matchesFilter;
  });

  const formatCurrency = (amount, currency) => {
    if (!amount) return 'N/A';
    return `${currency || 'USD'} ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-slate-100">Error</h3>
        <p className="mt-1 text-sm text-slate-500">{error}</p>
        <button
          onClick={fetchProjects}
          className="mt-4 btn btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100">Projects</h1>
        <div className="text-sm text-slate-500">
          {filteredProjects.length} of {projects.length} projects
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input"
            >
              <option value="all">All Types</option>
              <option value="fixed">Fixed Price</option>
              <option value="hourly">Hourly</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects List */}
      <div className="space-y-4">
        {filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-slate-100">No projects found</h3>
            <p className="mt-1 text-sm text-slate-500">
              {searchTerm || filterType !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'No projects have been processed yet.'}
            </p>
          </div>
        ) : (
          filteredProjects.map((project) => (
            <div key={project.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-slate-100">
                      {project.project_title || 'Untitled Project'}
                    </h3>
                    <span className={`status-badge ${
                      project.project_type === 'fixed' 
                        ? 'bg-primary-900/40 text-primary-200 border border-primary-800/40' 
                        : 'bg-warning-100 text-warning-800'
                    }`}>
                      {project.project_type || 'Unknown'}
                    </span>
                  </div>
                  
                  <p className="text-slate-400 mb-4 line-clamp-3">
                    {project.project_description || 'No description available'}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-400">Budget:</span>
                      <span className="font-medium">
                        {formatCurrency(project.minimum_budget, project.currency)}
                        {project.maximum_budget && project.maximum_budget !== project.minimum_budget && 
                          ` - ${formatCurrency(project.maximum_budget, project.currency)}`
                        }
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-400">Created:</span>
                      <span className="font-medium">{formatDate(project.created_at)}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Tag className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-400">Status:</span>
                      <span className={`status-badge ${
                        project.status === 'active' 
                          ? 'status-running' 
                          : project.status === 'bid_placed'
                          ? 'bg-primary-900/40 text-primary-200 border border-primary-800/40'
                          : 'status-stopped'
                      }`}>
                        {project.status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="ml-4 flex-shrink-0">
                  <a
                    href={`https://www.freelancer.com/projects/${project.project_id}/details`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary flex items-center space-x-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>View</span>
                  </a>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Projects;

